import { Component, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    ITCIApplicationOwner,
    ITCIApplicationTenancyRequest,
    TCIApplicationTenancyAction,
    TCITenantSelectorType,
    ITCIApplicationTenancyResponse,
    IAOTSCUsers,
    IAOApplicationOwner
} from "@tci/common";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details/ng-app-details-menu";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { ITCIAppDetailsMenuOutput } from "../../ng-tenant/tci-tenant-components";
import { BaseAppHMChangeOwnerService } from "./base-app-hm-change-owner.service";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_TRANSFER_OWNERSHIP.toString(),
    templateUrl: "base-app-hm-change-owner.component.html",
    styleUrls: ["./base-app-hm-change-owner.component.less"]
})
export class BaseAppHMChangeOwnerComponent<E, I extends BaseAppDetailMenuComponentInput,
    O extends BaseAppDetailMenuComponentOutput<ITCIAppDetailsMenuOutput>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit {

    private targetApp;
    private allUsersInOrg: IAOApplicationOwner[];
    private filteredUsers: IAOApplicationOwner[];

    private selectedUser: IAOApplicationOwner;

    // tslint:disable-next-line: member-ordering
    constructor(
        private changeOwnerService: BaseAppHMChangeOwnerService,
        private translateService: TranslateService,
        private toastr: NgToastrService
    ) {
        super();
    }

    ngOnInit(): void {
        const thisObj = this;
        this.changeOwnerService.getUsers().subscribe(
            (rsp: IAOTSCUsers) => {
                thisObj.allUsersInOrg = rsp.users;
                thisObj.filteredUsers = thisObj.allUsersInOrg;

                const inputObj = thisObj.input as BaseAppDetailMenuComponentInput;

                const appId = inputObj.application.id;
                const desiredInstance = inputObj.application.desiredInstanceCount;
                this.changeOwnerService.getAppStatus(appId, desiredInstance).subscribe(
                    (statusRsp) => {
                        let appStatus: string;
                        if (!statusRsp.success) {
                            appStatus = "error";
                        } else {
                            if (inputObj.application.info && inputObj.application.info.num_instances > 0) {
                                appStatus = (statusRsp.failedInstances > 0) ? "error" : "running";
                            } else {
                                appStatus = "stopped";
                            }
                        }

                        thisObj.targetApp = {
                            id: appId,
                            name: inputObj.application.name,
                            appStatusCodeClass: appStatus,
                            type: inputObj.application.type,
                            owner: inputObj.application.owner
                        };
                    }, (err) => {
                        thisObj.targetApp = {
                            id: appId,
                            name: inputObj.application.name,
                            appStatusCodeClass: "error",
                            type: inputObj.application.type,
                            owner: inputObj.application.owner
                        };
                    }
                );
            },
            (err) => {
                console.log("Fail to get users from current org: " + err);
            }
        );
    }

    selectUser(user) {
        if (this.targetApp.owner === user.userEntityId) {
            return false;
        }
        this.selectedUser = user;
    }

    filterUsers(value) {
        value = value.trim().toLowerCase();
        if (value) {
            const arr = value.split(" ").filter(Boolean);
            this.filteredUsers = this.allUsersInOrg.filter(tscUser => {
                const tscFirstName = tscUser.firstName.toLowerCase(),
                      tscLastName = tscUser.lastName.toLowerCase();
                if (arr.length > 1) {
                    const firstName = arr[0], lastName = arr.slice(1).join(" ");
                    return tscFirstName === firstName && tscLastName.indexOf(lastName) >= 0;
                } else {
                    return tscFirstName.indexOf(value) >= 0 || tscLastName.indexOf(value) >= 0;
                }
            });
        } else {
            this.filteredUsers = this.allUsersInOrg;
        }
    }

    changeOwner() {
        const thisObj = this;
        const inputObj = this.input as BaseAppDetailMenuComponentInput;

        // Findout original user name for audit histroy
        const originalUsrId = inputObj.application.owner;
        let originalFN;
        let originalLN;
        for (const usr of this.allUsersInOrg) {
            if (usr.userEntityId === originalUsrId) {
                originalFN = usr.firstName;
                originalLN = usr.lastName;
                break;
            }
        }

        if (originalUsrId === "jack") {
            // this is for test user transer case only, fix in test user code in future
            originalFN = "jack";
            originalLN = "jack";
        }

        const newOwer: ITCIApplicationOwner = {
            userEntityId: this.selectedUser.userEntityId,
            firstName: this.selectedUser.firstName,
            lastName: this.selectedUser.lastName,
            email: this.selectedUser.email,
            role: this.selectedUser.role,
            timestamp: this.selectedUser.timestamp,
            status: this.selectedUser.status,
            originalUser: {
                firstName: originalFN,
                lastName: originalLN,
                userId: originalUsrId,
            }
        };
        const appReq: ITCIApplicationTenancyRequest = {
            tenantAction: TCIApplicationTenancyAction.TENANT_TRANSFER_OWNERSHIP,
            tenantType: inputObj.tenantAppType,
            appId: inputObj.application.id,
            tenantOwner: newOwer
        };
        this.changeOwnerService.changeOwner(appReq).subscribe(
            (rsp: ITCIApplicationTenancyResponse) => {
                thisObj.toastr.clear();
                thisObj.toastr.success(rsp.msg);
                thisObj.closeModal(true);
            },
            (err) => {
                thisObj.toastr.clear();
                thisObj.toastr.error(err.msg || err.result.errorMsg);
                thisObj.closeModal(false);
            }
        );
    }

    cancel() {
        this.closeModal(false);
    }

    protected closeModal(refresh: boolean): ITCIAppDetailsMenuOutput {
        const e: ITCIAppDetailsMenuOutput = {
            menuItemSelector: this.input.componentSelector,
            closeModal: true,
            refresh
        };
        this.output.emit(e);
        return e;
    }
}
